<template>
  <!--
    NOTE:
    - 登录页需要在部分平板/浏览器下稳定可见（地址栏/工具栏/软键盘会改变可视高度）。
    - 由于 App 根节点使用了 overflow:hidden，这里使用 fixed + 自身滚动，避免被父级裁剪。
    - 学习机 WebView（如小度 P20）不支持 inset/100dvh，关键布局用 scoped 四边定位兜底。
  -->
  <div class="login-page pad-safe-top" :class="{ 'login-page--legacy': useLegacyLayout }">
    <!-- 视频背景（桌面端和平板横屏；旧 WebView 跳过；mp4 兼容 Android 学习机） -->
    <video
      v-if="showVideo"
      class="login-page__video"
      autoplay
      loop
      muted
      playsinline
      webkit-playsinline
      x5-playsinline
      disablePictureInPicture
      :controls="false"
      @contextmenu.prevent
      @dragstart.prevent
      @error="onVideoError"
    >
      <source :src="videoMp4Src" type="video/mp4">
    </video>

    <!-- Logo（仅平板横屏双栏时显示） -->
    <div
      v-if="isSidePanelLayout && isLandscape"
      class="login-page__landscape-logo"
    >
      <img
        :src="logoUrl"
        class="w-16 h-16 rounded-xl"
        :alt="siteTitle"
        @error="handleImageError"
      />
    </div>

    <!-- 横屏双栏：右侧登录面板 -->
    <div
      v-if="isSidePanelLayout"
      class="login-page__side-panel"
      :class="sidePanelClass"
    >
      <div class="login-page__side-inner">
        <user-login class="w-full min-w-0 max-w-full" />
      </div>
    </div>

    <!-- 非双栏：居中登录（旧 WebView / 竖屏平板 / 手机） -->
    <div v-else class="login-page__center" :class="centerPanelClass">
      <div class="login-page__center-inner">
        <div class="w-full flex flex-col items-center justify-center mb-5">
          <img
            :src="logoUrl"
            class="w-24 h-24 rounded-xl mb-2.5"
            :alt="siteTitle"
            @error="handleImageError"
          />
        </div>
        <user-login class="w-full min-w-0 max-w-full" />
      </div>
    </div>
  </div>
</template>

<script>
import UserLogin from '@/components/UserLogin'
import { isLegacyWebView } from '@/utils/legacyWebView'
import {
  shouldShowLoginVideo,
  shouldUseSidePanelLayout,
  resolveSidePanelVariant,
  detectLandscape
} from '@/views/login/loginPageLayout'

export default {
  name: 'Login',
  components: {
    UserLogin
  },
  data () {
    return {
      imageError: false,
      videoFailed: false,
      useLegacyLayout: isLegacyWebView(),
      videoMp4Src: require('@/assets/video/expore_001.mp4'),
      isLandscape: false
    }
  },
  computed: {
    logoUrl () {
      if (this.imageError) {
        return require('@/assets/logo.png')
      }
      const configLogo = this.$store.getters['common/systemConf']?.web_site_logo
      return configLogo || require('@/assets/logo.png')
    },
    siteTitle () {
      const configTitle = this.$store.getters['common/systemConf']?.web_site_title
      return configTitle || '词呗AI'
    },
    showVideo () {
      return shouldShowLoginVideo({
        useLegacyLayout: this.useLegacyLayout,
        videoFailed: this.videoFailed,
        isDesktop: this.isDesktopDevice,
        isTablet: this.isTabletDevice,
        isLandscape: this.isLandscape
      })
    },
    isSidePanelLayout () {
      return shouldUseSidePanelLayout({
        useLegacyLayout: this.useLegacyLayout,
        isDesktop: this.isDesktopDevice,
        isTablet: this.isTabletDevice,
        isLandscape: this.isLandscape
      })
    },
    sidePanelClass () {
      const variant = resolveSidePanelVariant({
        isTablet: this.isTabletDevice,
        isLandscape: this.isLandscape
      })
      return variant === 'tablet'
        ? 'login-page__side-panel--tablet'
        : 'login-page__side-panel--desktop'
    },
    centerPanelClass () {
      return 'login-page__center--full'
    }
  },
  mounted () {
    this.checkOrientation()
    window.addEventListener('resize', this.checkOrientation)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.checkOrientation)
  },
  methods: {
    checkOrientation () {
      this.isLandscape = detectLandscape()
    },
    handleImageError () {
      this.imageError = true
    },
    onVideoError () {
      this.videoFailed = true
    }
  }
}
</script>

<style scoped>
.login-page {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: #f8fafc;
  box-sizing: border-box;
}

.dark .login-page {
  background-color: #020617;
}

.login-page__video {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.login-page__landscape-logo {
  position: fixed;
  top: 1.5rem;
  top: max(1.5rem, env(safe-area-inset-top, 0px));
  left: 1.5rem;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.login-page__side-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border-left: 1px solid rgba(124, 58, 237, 0.1);
  box-sizing: border-box;
}

.dark .login-page__side-panel {
  background-color: #0f172a;
  border-left-color: rgba(124, 58, 237, 0.4);
}

.login-page__side-panel--tablet {
  width: 350px;
  max-width: 100%;
  padding: 2rem;
}

.login-page__side-panel--desktop {
  width: 500px;
  max-width: 100%;
  padding: 3rem;
}

.login-page__side-inner {
  width: 100%;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  padding-top: 2rem;
  padding-bottom: 2rem;
  box-sizing: border-box;
}

.login-page__center {
  position: relative;
  z-index: 20;
  width: 100%;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

.login-page__center--full {
  background-color: #ffffff;
}

.dark .login-page__center--full {
  background-color: #0f172a;
}

.login-page__center-inner {
  width: 100%;
  max-width: 480px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.25rem;
  box-sizing: border-box;
}

.login-page--legacy .login-page__center-inner {
  max-width: 560px;
}
</style>
